import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Pagination,
  Button,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ScaleIcon from '@mui/icons-material/Scale';

const UnitList = ({ onEdit, onAdd }) => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = () => {
    setLoading(true);
    axios.get('/api/units')
      .then(res => setUnits(res.data.data || res.data))
      .catch(err => console.error('Error loading units:', err))
      .finally(() => setLoading(false));
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await axios.delete(`/api/units/${id}`);
        setUnits(units.filter(unit => unit.id !== id));
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete unit');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredUnits = units.filter(unit =>
    unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    unit.short_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUnits = filteredUnits.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredUnits.length / itemsPerPage);

  return (
    <Box>
      <Box sx={{ 
        mb: 1.5, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2
      }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600,
            color: '#424242',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '0.875rem'
          }}
        >
          <ScaleIcon sx={{ fontSize: 18 }} />
          All Units ({filteredUnits.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              fontSize: '0.8rem',
              width: '180px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                }
              }
            }}
          />
          <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={onAdd}
          size="small"
          sx={{ 
            bgcolor: '#00acc1',
            px: 2,
            py: 0.5,
            fontSize: '0.75rem',
            textTransform: 'none',
            '&:hover': { 
              bgcolor: '#00838f'
            }
          }}
        >
          Add Unit
        </Button>
        </Box>
      </Box>

      {units.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
          <Typography color="text.secondary">
            No units found. Create your first unit above.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: '#f5f5f5'
                }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Short Code</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Parent ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Conversion</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUnits.map((unit) => (
                  <TableRow 
                    key={unit.id}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'action.hover'
                      },
                      '& .MuiTableCell-root': {
                        py: 0.75,
                        px: 1.5,
                        fontSize: '0.8rem'
                      }
                    }}
                  >
                    <TableCell>{unit.id}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                        {unit.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: '#00acc1', fontSize: '0.8rem' }}>
                        {unit.short_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                        {unit.parent_id ? `${unit.parent_id} - ${unit.parent_name || 'N/A'}` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        {unit.conversion || 1}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        onClick={() => onEdit(unit)}
                        size="small"
                        sx={{ 
                        color: '#1976d2',
                        '&:hover': { 
                          bgcolor: '#e3f2fd'
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(unit.id, unit.name)}
                        size="small"
                        sx={{ 
                          color: '#d32f2f',
                          ml: 1,
                          '&:hover': { 
                            bgcolor: '#ffebee'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={totalPages} 
                page={currentPage} 
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default UnitList;
